import React, { memo, useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import Popover from '@mui/material/Popover';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { makeStyles } from 'tss-react/mui';

import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';

import { entitlementsSelectors } from 'app-components/flux/entitlements';
import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import { rootUiActions, rootUiTypes } from 'app-components/flux/root-ui';
import { ssoActions } from 'app-components/flux/sso';
import { getBuildConfig, isDemoMode } from 'app-components/utils/core';
import { useAppSelector } from 'hooks/redux';

import { useGlobalCobrowseRequired } from 'utils/cobrowse';
import { ENVIRONMENT } from 'utils/core/config';
import { COBROWSE_CODE_FETCH_DIALOG_ID, cobrowseCodeFetchDialog } from 'components/Dialogs/CobrowseCodeFetch/actions';
import { DIALOG_TYPE_EMPTY_DIALOG } from 'components/Dialogs/EmptyDialog';
import ZeroStateView from 'components/ZeroStateView';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';

import HelpIcon from 'assets/icons/nav-icons/help.inline.svg';
import SupportIcon from 'assets/icons/nav-icons/support.inline.svg';
import UserGuideIcon from 'assets/icons/nav-icons/userGuide.inline.svg';
import CommunityIcon from 'assets/icons/nav-icons/community.inline.svg';
import DemoIcon from 'assets/icons/nav-icons/demo.inline.svg';
import ScreenShareIcon from 'assets/icons/nav-icons/screenShare.inline.svg';

const POPOVER_WIDTH = 320;

const useStyles = makeStyles()((theme) => ({
  helpPopover: {
    minWidth: POPOVER_WIDTH,
    padding: `${theme.spacing(2)} ${theme.spacing(1)}`,
  },
  listItems: {
    padding: 0,
  },
}));

type TopBarHelpMenuProps = {
  hideNavButtons: boolean;
  id?: string;
};

const TopBarHelpMenu = memo(function TopBarHelpMenu({ hideNavButtons, id }: TopBarHelpMenuProps) {
  const [anchorEl, setAnchorEl] = useState<null | Element>(null);
  const { classes } = useStyles();
  const dispatch = useDispatch();

  const intercomEnabled = useAppSelector((state) => featureFlagsSelectors.getFeatureFlag(state, 'intercom'));
  const userGuideEnabled = useAppSelector((state) => featureFlagsSelectors.getFeatureFlag(state, 'userGuideEnabled'));
  const profileHelpSupport = useAppSelector((state) =>
    featureFlagsSelectors.getFeatureFlags(state).get('profileHelpSupport'),
  );
  const lab = useAppSelector((state) => featureFlagsSelectors.getFeatureFlag(state, 'lab'));
  const cobrowseSession = useAppSelector((state) => featureFlagsSelectors.getFeatureFlag(state, 'cobrowseSession'));
  const datasetEntitlement = useSelector(entitlementsSelectors.getTopTierEntitlement);

  const [, setCobrowseRequired] = useGlobalCobrowseRequired();

  const demoUrl = useMemo(() => {
    const entitlement = datasetEntitlement?.id;
    const key = `${ENVIRONMENT}:${entitlement}`;
    const urls: Record<string, string> = {
      'prod:ACME_BASIC': 'https://simplifi-demo.quicken.com',
      'preprod:ACME_BASIC': 'https://simplifi-demo-preprod.quickencs.com',
      'stg:ACME_BASIC': 'https://simplifi-demo-stg.quickencs.com',
      'qa:ACME_BASIC': 'https://simplifi-demo-qa.quickencs.com',
      'bld:ACME_BASIC': 'https://simplifi-demo-bld.quickencs.com',
    };
    return urls[key];
  }, [datasetEntitlement?.id]);

  const helpCenterEnabled =
    intercomEnabled || userGuideEnabled || profileHelpSupport || cobrowseSession || (lab && demoUrl);

  const closeHelp = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const triggerIntercom = useCallback(() => {
    const launcher = document.getElementById('intercom-launcher');
    if (launcher instanceof HTMLElement) {
      launcher.click();
    }
  }, []);

  const handleIconClick = (event: React.SyntheticEvent) => {
    if (!hideNavButtons && intercomEnabled) {
      // Normal nav state: behave like the old Help button and just open Intercom.
      triggerIntercom();
      return;
    }

    setAnchorEl(event.currentTarget as Element);
  };

  const helpClick = () => {
    closeHelp();
    window.open('https://www.quicken.com/support');
  };

  const onDemoModeClick = () => {
    if (demoUrl) {
      closeHelp();
      window.open(demoUrl);
    }
  };

  const onScreenShareClick = () => {
    setCobrowseRequired(true as any);

    dispatch(
      cobrowseCodeFetchDialog({
        onClose() {
          dispatch(rootUiActions.removeDialog(COBROWSE_CODE_FETCH_DIALOG_ID));
        },
      }),
    );

    closeHelp();
  };

  const goToCommunity = () => {
    dispatch(rootUiActions.removeDialog('community-verify-email'));
    closeHelp();
    new Promise((resolve, reject) => {
      dispatch(
        ssoActions.getSSOToken(undefined, {
          resolve,
          reject,
          axiosConfig: {
            params: {
              clientId: getBuildConfig().sso_client_id,
              redirect_uri: getBuildConfig().community_url,
            },
          },
        }),
      );
    })
      .then((response: { data: { errors: { code?: string }[]; redirectUri: string } }) => {
        if (response?.data?.errors?.[0].code === 'QCS-0401-7') {
          dispatch(
            rootUiActions.createDialog(
              rootUiTypes.mkRootUiData({
                id: 'community-verify-email',
                type: DIALOG_TYPE_EMPTY_DIALOG,
                allowNesting: true,
                props: new Map([
                  [
                    'content',
                    <ZeroStateView
                      key='community-verify-email'
                      qIllustration='businessBilled'
                      title='Verify your email address'
                      description='We sent you an email to confirm your identity. Once verified, click here to try again.'
                      size='small'
                      button={
                        <Button variant='contained' onClick={goToCommunity}>
                          Go to community
                        </Button>
                      }
                    />,
                  ],
                ]),
              }),
            ),
          );
        } else if (response?.data?.redirectUri) {
          setTimeout(() => window.open(response.data.redirectUri, '_blank'), 1000);
        }
      })
      .catch(() => {
        // Swallow errors and avoid breaking the help center.
      });
  };

  if (!helpCenterEnabled) {
    return null;
  }

  return (
    <>
      {/* Hidden launcher for Intercom custom_launcher_selector */}
      <IconButton
        id='intercom-launcher'
        aria-hidden
        sx={{ position: 'absolute', opacity: 0, pointerEvents: 'none', width: 0, height: 0 }}
      />

      <Tooltip title='Help center'>
        <IconButton
          variant='tonal'
          shape='rounded'
          id={id ?? 'topbar-help'}
          size='medium'
          aria-label='Help center'
          aria-haspopup='menu'
          aria-expanded={!!anchorEl}
          onClick={handleIconClick}
        >
          <HelpIcon width={20} height={20} />
        </IconButton>
      </Tooltip>

      <Popover
        id='help-center-menu'
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={closeHelp}
        anchorOrigin={{
          horizontal: 'right',
          vertical: 'bottom',
        }}
        transformOrigin={{
          horizontal: 'right',
          vertical: 'top',
        }}
        slotProps={{
          paper: {
            elevation: 24,
            style: {
              width: POPOVER_WIDTH,
              transform: 'translate3d(0, 0, 0)',
            },
          },
        }}
      >
        <div className={classes.helpPopover}>
          <Typography variant='titleMedium' sx={{ paddingX: 2, paddingBottom: 1 }}>
            Help center
          </Typography>
          <MenuList className={classes.listItems}>
            {intercomEnabled && (
              <MenuItem
                id='help-chat-with-support'
                onClick={() => {
                  closeHelp();
                  triggerIntercom();
                }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <SupportIcon width={20} height={20} />
                </ListItemIcon>
                <ListItemText
                  primary='Contact support'
                  slotProps={{
                    primary: { variant: 'paragraphRegular' },
                  }}
                />
              </MenuItem>
            )}

            {userGuideEnabled && (
              <MenuItem
                id='help-quicken-university'
                onClick={() => {
                  closeHelp();
                  window.dispatchEvent(new CustomEvent('user-guide:open', { detail: { guideUrl: '/' } }));
                }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <UserGuideIcon width={20} height={20} />
                </ListItemIcon>
                <ListItemText
                  primary='Quicken University'
                  slotProps={{
                    primary: { variant: 'paragraphRegular' },
                  }}
                />
              </MenuItem>
            )}

            {(profileHelpSupport || cobrowseSession || (!isDemoMode() && lab && demoUrl)) && (
              <Divider variant='middle' />
            )}

            <MenuItem id='help-community' onClick={goToCommunity}>
              <ListItemIcon sx={{ minWidth: 40 }}>
                <CommunityIcon width={20} height={20} />
              </ListItemIcon>
              <ListItemText
                primary='Quicken Community'
                slotProps={{
                  primary: { variant: 'paragraphRegular' },
                }}
              />
              <OpenInNewRoundedIcon sx={{ fontSize: '16px', color: 'icon.disabled' }} />
            </MenuItem>

            <Divider variant='middle' />

            {profileHelpSupport && (
              <MenuItem id='help-support' onClick={helpClick}>
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <OpenInNewRoundedIcon fontSize='small' />
                </ListItemIcon>
                <ListItemText
                  primary='Help & Support'
                  slotProps={{
                    primary: { variant: 'paragraphRegular' },
                  }}
                />
              </MenuItem>
            )}

            {!isDemoMode() && lab && demoUrl && (
              <MenuItem id='help-live-demo' onClick={onDemoModeClick}>
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <DemoIcon width={20} height={20} />
                </ListItemIcon>
                <ListItemText
                  primary='Live Demo'
                  slotProps={{
                    primary: { variant: 'paragraphRegular' },
                  }}
                />
                <Box display='flex' alignItems='center' gap={1}>
                  <Chip
                    icon={<DemoIcon width={16} height={16} />}
                    label='Test Drive'
                    size='small'
                    color='success'
                    variant='tonal'
                  />
                  <OpenInNewRoundedIcon sx={{ fontSize: '16px', color: 'icon.disabled' }} />
                </Box>
              </MenuItem>
            )}

            {cobrowseSession && (
              <MenuItem id='help-share-screen' onClick={onScreenShareClick}>
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <ScreenShareIcon width={20} height={20} />
                </ListItemIcon>
                <ListItemText
                  primary='Share Screen'
                  slotProps={{
                    primary: { variant: 'paragraphRegular' },
                  }}
                />
              </MenuItem>
            )}
          </MenuList>
        </div>
      </Popover>
    </>
  );
});

export default TopBarHelpMenu;
